import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Button } from "../../components/Button";
import * as Fonts from "../../components/Fonts";
import Input from "../../components/Form/Input/input";
import { Select } from "../../components/Form/Input/select";
import Label from "../../components/Form/Label";
import PhoneNumberInput from "../../components/Form/PhoneNumberInput";
import { FormGroup } from "../../components/Form/styles";
import SaveBar from "../../components/SaveBar";
import { notifySaveError } from "../../components/SaveErrorNotice";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import ToggleSwitch from "../../components/ToggleSwitch";
import constants from "../../constants";
import { useAppState } from "../../contexts/App";
import { useSite } from "../../contexts/Site";
import { siteHasAgendaPage, withAgendaInMenu, withAgendaOutOfMenu } from "../../model/pages";
import { AgendaSettings, getAgenda, saveAgenda } from "../../services/agenda";
import { api, hasActiveSubscription } from "../../services/api";
import { isApiError } from "../../services/api/errors";
import { getPayments, PaymentsState, updatePayments } from "../../services/payments";
import { Card, ToggleRow } from "../Settings/styles";
import { BlockedDays } from "./BlockedDays";
import { formatPhone } from "./format";
import { FieldError, Note, SectionHead } from "./styles";
import { allTimezones, BRAZIL_TIMEZONES, cityLabel, offsetOf, TimezoneNotice } from "./timezone";
import { copyMonday, fromApiEnd, isDayValid, toApiEnd, WeekHours } from "./WeekHours";

const prefix = "pages.agenda.settings";
const paymentsPrefix = "pages.settings.payments";

const EMPTY_WEEK: AgendaSettings["week"] = { "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] };

const MIN_NOTICE = [0, 30, 60, 120, 240, 720, 1440, 2880];
const BUFFER = [0, 5, 10, 15, 20, 30, 45, 60];
const CANCEL_UNTIL = [0, 60, 120, 360, 720, 1440, 2880];

/** What the screen changes (not the fields of the booking: for now the owner doesn't change them). */
type Editable = Pick<
  AgendaSettings,
  | "enabled"
  | "timezone"
  | "notifyPhone"
  | "confirmation"
  | "minNoticeMinutes"
  | "maxDaysAhead"
  | "bufferMinutes"
  | "cancelUntilMinutes"
  | "week"
  | "blockedDates"
  | "blockedTimes"
>;

/** The same week or blocks with each end of an interval through `change` (the API's 24:00 and the pickers' 23:59). */
const withEnds = <T extends [string, string]>(intervals: T[], change: (time: string) => string): T[] =>
  intervals.map(([from, to]) => [from, change(to)] as T);
const weekWithEnds = (week: AgendaSettings["week"], change: (time: string) => string) =>
  Object.fromEntries(Object.entries(week).map(([day, intervals]) => [day, withEnds(intervals, change)]));
const timesWithEnds = (times: AgendaSettings["blockedTimes"], change: (time: string) => string) =>
  Object.fromEntries(
    Object.entries(times).map(([date, [from, to]]) => [date, [from, change(to)]])
  ) as AgendaSettings["blockedTimes"];

const editableOf = (settings: AgendaSettings): Editable => ({
  enabled: settings.enabled,
  timezone: settings.timezone,
  notifyPhone: settings.notifyPhone ?? null,
  confirmation: settings.confirmation,
  minNoticeMinutes: settings.minNoticeMinutes,
  maxDaysAhead: settings.maxDaysAhead,
  bufferMinutes: settings.bufferMinutes,
  cancelUntilMinutes: settings.cancelUntilMinutes,
  // every day, also the ones an older answer left out
  week: weekWithEnds({ ...EMPTY_WEEK, ...settings.week }, fromApiEnd),
  blockedDates: settings.blockedDates ?? [],
  blockedTimes: timesWithEnds(settings.blockedTimes ?? {}, fromApiEnd),
});

type Props = {
  siteId: string;
  /** Says whether there are changes not saved yet (the page's header asks before leaving with them). */
  onUnsavedChange?: (hasChanges: boolean) => void;
};

/** The agenda's settings: on or off, the hours of the week, the rules and the days off. */
export const Settings = ({ siteId, onUnsavedChange }: Props) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState<Editable | null>();
  const [holidays, setHolidays] = useState<Record<string, string>>({});
  // the contact form's WhatsApp (receives the requests until another is saved) and whether the page is published
  const [formPhone, setFormPhone] = useState<string | null>(null);
  const [pagePublished, setPagePublished] = useState(true);
  const [draft, setDraft] = useState<Editable>();
  const [maxDays, setMaxDays] = useState("");
  const [invalidDays, setInvalidDays] = useState<string[]>([]);
  const [status, setStatus] = useState<"saving" | "maxDays" | null>(null);
  const toast = useToast();
  const { site, setSite } = useSite();
  const { appState, setAppState } = useAppState();
  const navigate = useNavigate();
  // Charging online in the agenda: its switch (with the registration approved, or to turn it off), or else a
  // shortcut to Cobranças' bank details. Any failure to load just leaves it out
  const [payments, setPayments] = useState<PaymentsState>();
  const [savingOnline, setSavingOnline] = useState(false);
  const agendaOnline = !!payments?.account.agendaOnline;
  const approved = !!payments?.eligible && payments.recipient?.status === "approved";

  useEffect(() => {
    if (!constants.payments) return;
    let current = true;
    setPayments(undefined);
    getPayments(siteId)
      .then(state => current && setPayments(state))
      .catch(() => current && setPayments(undefined));
    return () => {
      current = false;
    };
  }, [siteId]);

  // Saved at once, apart from the rest of the screen (not with the bar's Salvar), and not optimistic: the switch waits
  // for the API. A toast says it's on or off, or why it couldn't; without PLUS it's on, but only applies once the Keepo
  // is PLUS and published
  const saveOnline = async (value: boolean) => {
    setSavingOnline(true);
    try {
      setPayments(await updatePayments(siteId, { agendaOnline: value }));
      const message = !value
        ? "turnedOff"
        : hasActiveSubscription(appState.editingSiteSubscription)
          ? "turnedOn"
          : "turnedOnNoPlus";
      toast.success(t(`${paymentsPrefix}.charging.${message}`));
    } catch (error) {
      const reason = isApiError(error, "RECIPIENT_NOT_APPROVED") ? "notApproved" : "generic";
      toast.error(t(`${paymentsPrefix}.charging.errors.${reason}`));
    } finally {
      setSavingOnline(false);
    }
  };

  useEffect(() => {
    let current = true;
    setSaved(undefined);
    getAgenda(siteId)
      .then(settings => {
        if (!current) return;
        const editable = editableOf(settings);
        setHolidays(settings.holidays ?? {});
        setFormPhone(settings.formPhone ?? null);
        setPagePublished(settings.pagePublished !== false);
        setSaved(editable);
        setDraft(editable);
        setMaxDays(String(editable.maxDaysAhead));
      })
      .catch(() => current && setSaved(null));
    return () => {
      current = false;
    };
  }, [siteId]);

  // anything to save: the bar shows (fixed at the bottom), as in the editor, and leaving asks first
  const hasChanges =
    !!saved && !!draft && (JSON.stringify(draft) !== JSON.stringify(saved) || maxDays !== String(saved.maxDaysAhead));
  useEffect(() => {
    onUnsavedChange?.(hasChanges);
  }, [hasChanges, onUnsavedChange]);

  if (saved === undefined) return <Skeleton className="mt-4 h-40 w-full rounded-2xl" />;
  if (saved === null || !draft) return <Note role="alert">{t(`${prefix}.loadError`)}</Note>;

  // set up on any Keepo: PLUS is asked when publishing (only charging online needs it now)
  const disabled = status === "saving";
  // the page is in the menu of the Keepo being edited, not published yet
  const inDraft = siteHasAgendaPage(site);
  const set = <K extends keyof Editable>(key: K, value: Editable[K]) => {
    setStatus(null);
    setDraft(current => current && { ...current, [key]: value });
  };

  const save = async () => {
    const badDays = Object.entries(draft.week)
      .filter(([, intervals]) => !isDayValid(intervals))
      .map(([day]) => day);
    setInvalidDays(badDays);
    const days = Number(maxDays);
    if (!Number.isInteger(days) || days < 1 || days > 365) return setStatus("maxDays");
    if (badDays.length) return;

    setStatus("saving");
    try {
      const answer = await saveAgenda(siteId, {
        ...draft,
        maxDaysAhead: days,
        week: weekWithEnds(draft.week, toApiEnd),
        blockedTimes: timesWithEnds(draft.blockedTimes, toApiEnd),
      });
      const settings = editableOf(answer);
      setFormPhone(answer.formPhone ?? null);
      setPagePublished(answer.pagePublished !== false);
      setSaved(settings);
      setDraft(settings);
      setStatus(null);
      // turned on: its page comes into the menu of the Keepo's draft, to be seen in the preview (publishing is theirs)
      const withPage =
        !saved.enabled && settings.enabled && withAgendaInMenu(site, t("pages.editor.pages.agenda.title"));
      // turned off: its page leaves the menu (the same switch as the page's eye on Páginas)
      const withoutPage = saved.enabled && !settings.enabled && withAgendaOutOfMenu(site);
      const nextSite = withPage || withoutPage;
      if (nextSite) {
        setSite(nextSite);
        setAppState({ ...appState, editingSiteModified: true });
        api.saveDraft(siteId, nextSite).catch(notifySaveError);
      }
      // the Agenda turned on or off: the toast says so (with the page that came into the menu)
      const message =
        saved.enabled === settings.enabled
          ? "saved"
          : !settings.enabled
            ? "turnedOff"
            : withPage
              ? "turnedOnWithPage"
              : // without PLUS it's on, but customers only book once the Keepo is PLUS and published; with it, once its
                // page is on the air (the API says whether it is; an older one doesn't, and it was always "now")
                !hasActiveSubscription(appState.editingSiteSubscription)
                ? "turnedOnNoPlus"
                : answer.pagePublished !== false
                  ? "turnedOn"
                  : "turnedOnUnpublished";
      toast.success(t(`${prefix}.${message}`));
    } catch {
      setStatus(null);
      toast.error(t(`${prefix}.saveError`));
    }
  };

  /** 30 minutos, 2 horas, 1 dia */
  const duration = (minutes: number) =>
    minutes % 1440 === 0
      ? t(`${prefix}.units.days`, { count: minutes / 1440 })
      : minutes % 60 === 0
        ? t(`${prefix}.units.hours`, { count: minutes / 60 })
        : t(`${prefix}.units.minutes`, { count: minutes });

  const select = (id: string, key: "minNoticeMinutes" | "bufferMinutes" | "cancelUntilMinutes", options: number[]) => (
    <FormGroup>
      <Select id={id} value={draft[key]} disabled={disabled} onChange={event => set(key, Number(event.target.value))}>
        {/* a value saved elsewhere (the API takes any in its range) stays as it is */}
        {(options.includes(draft[key]) ? options : [...options, draft[key]].sort((a, b) => a - b)).map(value => (
          <option key={value} value={value}>
            {value === 0 ? t(`${prefix}.${key}.none`) : t(`${prefix}.${key}.option`, { time: duration(value) })}
          </option>
        ))}
      </Select>
      <Label htmlFor={id} text={t(`${prefix}.${key}.label`)} ignoreLengthInfo />
    </FormGroup>
  );

  const discard = () => {
    setDraft(saved);
    setMaxDays(String(saved.maxDaysAhead));
    setInvalidDays([]);
    setStatus(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <ToggleRow>
          <div className="text">
            <h6>{t(`${prefix}.enabled`)}</h6>
            <p className="description">{t(`${prefix}.enabledHint`)}</p>
          </div>
          <ToggleSwitch
            value={draft.enabled}
            setValue={value => set("enabled", value)}
            disabled={disabled}
            label={t(`${prefix}.enabled`)}
          />
        </ToggleRow>
        {/* on, but without the page a customer books on: only the owner books, in the editor */}
        {saved.enabled && !pagePublished && (
          <Hint>
            {inDraft ? (
              <>
                {t(`${prefix}.pageDraftHint`)}{" "}
                <Link to="/editor/style?flow=publish" className="underline">
                  {t(`${prefix}.publishLink`)}
                </Link>
              </>
            ) : (
              t(`${prefix}.pageHint`)
            )}
          </Hint>
        )}
      </Card>

      {payments && (approved || agendaOnline) && (
        <Card>
          <ToggleRow>
            <div className="text">
              <h6>{t(`${paymentsPrefix}.charging.agendaOnline`)}</h6>
              <p className="description">
                {t(
                  `${paymentsPrefix}.charging.${!payments.eligible ? "onlyOff" : approved ? "agendaOnlineHint" : "waitApproval"}`
                )}
              </p>
            </div>
            {/* Without the registration approved (or out of reais): only turning it off */}
            <ToggleSwitch
              value={agendaOnline}
              setValue={saveOnline}
              // set up freely, PLUS or not (customers only book on a PLUS Keepo, which publishing asks for); turning it on
              // needs the registration approved, turning it off never waits
              disabled={savingOnline || (!approved && !agendaOnline)}
              label={t(`${paymentsPrefix}.charging.agendaOnline`)}
            />
          </ToggleRow>
        </Card>
      )}
      {payments && payments.eligible && !approved && !agendaOnline && (
        <Card>
          <ToggleRow>
            <div className="text">
              <h6>{t(`${prefix}.online.title`)}</h6>
              <p className="description">{t(`${prefix}.online.hint`)}</p>
            </div>
            <Button
              type="INFO"
              size="MD"
              role="button"
              tabIndex={0}
              className="shrink-0"
              onClick={() => navigate("/cobrancas/dados-bancarios")}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate("/cobrancas/dados-bancarios");
                }
              }}>
              {t(`${prefix}.online.activate`)}
            </Button>
          </ToggleRow>
        </Card>
      )}

      <section className="flex flex-col gap-3">
        {/* the title, and on its right a small shortcut */}
        <SectionHead>
          <Fonts.Label>{t(`${prefix}.hours`)}</Fonts.Label>
          {!disabled && (draft.week["1"] ?? []).length > 0 && (
            <button type="button" onClick={() => set("week", copyMonday(draft.week))}>
              {t(`${prefix}.week.copyMondayShort`)}
            </button>
          )}
        </SectionHead>
        <Card>
          <WeekHours
            week={draft.week}
            disabled={disabled}
            invalidDays={invalidDays}
            onChange={week => set("week", week)}
          />
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <Fonts.Label>{t(`${prefix}.rules`)}</Fonts.Label>
        <Card>
          <Rules>
            <FormGroup className="wide">
              {/* the group shows its children upside down: the hint goes under the field */}
              {constants.payments && agendaOnline && draft.confirmation === "manual" && (
                <Hint>{t(`${prefix}.confirmation.onlineHint`)}</Hint>
              )}
              <Select
                id="agenda-confirmation"
                value={draft.confirmation}
                disabled={disabled}
                onChange={event => set("confirmation", event.target.value as Editable["confirmation"])}>
                <option value="auto">{t(`${prefix}.confirmation.auto`)}</option>
                <option value="manual">{t(`${prefix}.confirmation.manual`)}</option>
              </Select>
              <Label htmlFor="agenda-confirmation" text={t(`${prefix}.confirmation.label`)} ignoreLengthInfo />
            </FormGroup>
            {/* where the requests waiting for approval arrive, with buttons to accept or decline them */}
            {draft.confirmation === "manual" && (
              <FormGroup className="wide">
                <Hint>{t(`${prefix}.notifyPhone.hint`)}</Hint>
                <PhoneNumberInput
                  id="agenda-notify-phone"
                  label={t(`${prefix}.notifyPhone.label`)}
                  value={draft.notifyPhone ?? ""}
                  disabled={disabled}
                  onChange={value => set("notifyPhone", value || null)}
                />
                {formPhone && !draft.notifyPhone && (
                  <Hint>{t(`${prefix}.notifyPhone.formFallback`, { phone: formatPhone(formPhone) })}</Hint>
                )}
              </FormGroup>
            )}
            {/* the group shows its children upside down: the hint and the notice go under the field */}
            <FormGroup className="wide">
              <TimezoneNotice timezone={draft.timezone} />
              <Hint>{t(`${prefix}.timezone.hint`)}</Hint>
              <Select
                id="agenda-timezone"
                value={draft.timezone}
                disabled={disabled}
                onChange={event => set("timezone", event.target.value)}>
                <optgroup label={t(`${prefix}.timezone.brazil`)}>
                  {BRAZIL_TIMEZONES.map(zone => (
                    <option key={zone} value={zone}>
                      {t(`${prefix}.timezone.zones.${zone.split("/")[1]}`, { offset: offsetOf(zone) })}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={t(`${prefix}.timezone.others`)}>
                  {/* the saved one stays in the list, even when this browser doesn't know it */}
                  {[...new Set([...allTimezones(), draft.timezone])]
                    .filter(zone => !BRAZIL_TIMEZONES.includes(zone))
                    .sort()
                    .map(zone => (
                      <option key={zone} value={zone}>
                        {cityLabel(zone)}
                      </option>
                    ))}
                </optgroup>
              </Select>
              <Label htmlFor="agenda-timezone" text={t(`${prefix}.timezone.label`)} ignoreLengthInfo />
            </FormGroup>
            {select("agenda-min-notice", "minNoticeMinutes", MIN_NOTICE)}
            <FormGroup>
              {status === "maxDays" && <FieldError role="alert">{t(`${prefix}.maxDaysInvalid`)}</FieldError>}
              <Input
                id="agenda-max-days"
                type="text"
                label={t(`${prefix}.maxDaysAhead`)}
                value={maxDays}
                disabled={disabled}
                ignoreLengthInfo
                onChange={event => {
                  setStatus(null);
                  setMaxDays(event.target.value.replace(/\D/g, ""));
                }}
              />
            </FormGroup>
            {select("agenda-buffer", "bufferMinutes", BUFFER)}
            {select("agenda-cancel-until", "cancelUntilMinutes", CANCEL_UNTIL)}
          </Rules>
        </Card>
      </section>

      <BlockedDays
        dates={draft.blockedDates}
        times={draft.blockedTimes}
        holidays={holidays}
        timezone={draft.timezone}
        disabled={disabled}
        onChange={(dates, times) => {
          set("blockedDates", dates);
          set("blockedTimes", times);
        }}
      />

      {hasChanges && (
        <SaveBar
          sticky
          flush
          saving={status === "saving"}
          onSave={() => status !== "saving" && save()}
          cancelTitle={t(`${prefix}.discard`)}
          onCancelClick={discard}
          hasUnsavedChanges={hasChanges}
          onConfirmExitUnsavedChanges={discard}
        />
      )}
    </div>
  );
};

/** The rules in two columns (the confirmation on a line of its own); one column on phones. */
const Rules = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: end;

  > * {
    margin-bottom: 0;
  }

  .wide {
    grid-column: 1 / -1;
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** A line under a field. */
const Hint = styled.p`
  margin-top: 0.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  color: var(--color-neutral-150);
`;
